import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { Mail, MapPin, Send } from 'lucide-react';

import { useToast } from '@/hooks/use-toast';
import { PublicShell } from '@/components/public-shell';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';

export default function Contact() {
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: '',
    type: 'general',
  });

  // Simulate contact form submission
  const contactMutation = useMutation({
    mutationFn: async (_contactData: any) => {
      // In a real app, this would send to a contact API endpoint
      return new Promise(resolve => {
        setTimeout(resolve, 1000);
      });
    },
    onSuccess: () => {
      toast({
        title: 'Message Sent!',
        description: "Thank you for your message. We'll follow up by email.",
      });
      setFormData({
        name: '',
        email: '',
        subject: '',
        message: '',
        type: 'general',
      });
    },
    onError: () => {
      toast({
        title: 'Error',
        description: 'Failed to send message. Please try again or contact us directly via email.',
        variant: 'destructive',
      });
    },
  });

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.name.trim()) {
      toast({
        title: 'Missing Name',
        description: 'Please enter your name.',
        variant: 'destructive',
      });
      return;
    }

    if (!formData.email.trim()) {
      toast({
        title: 'Missing Email',
        description: 'Please enter your email address.',
        variant: 'destructive',
      });
      return;
    }

    if (!formData.subject.trim()) {
      toast({
        title: 'Missing Subject',
        description: 'Please enter a subject for your message.',
        variant: 'destructive',
      });
      return;
    }

    if (!formData.message.trim()) {
      toast({
        title: 'Missing Message',
        description: 'Please enter your message.',
        variant: 'destructive',
      });
      return;
    }

    contactMutation.mutate(formData);
  };

  return (
    <PublicShell routeKey="contact">
      {/* Header */}
      <section className="bg-muted py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl md:text-5xl font-bold text-foreground mb-4">Get in Touch</h1>
          <p className="text-xl text-muted-foreground mb-8">
            Questions, feedback, or ideas? We'd love to hear from you.
          </p>

          <div className="flex justify-center">
            <Badge className="bg-primary/10 text-primary px-4 py-2">Email-first support</Badge>
          </div>
        </div>
      </section>

      <div className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-3 gap-12">
            {/* Contact Information */}
            <div className="space-y-8">
              <div>
                <h2 className="text-2xl font-bold text-foreground mb-6">Contact Information</h2>

                <div className="space-y-6">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center flex-shrink-0">
                      <Mail className="w-6 h-6 text-primary" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-foreground mb-1">Email</h3>
                      <p className="text-muted-foreground text-sm mb-2">
                        General inquiries, support, creator questions, and feedback
                      </p>
                      <a
                        href="mailto:admin@kalovate.com"
                        className="text-primary hover:underline font-medium"
                      >
                        admin@kalovate.com
                      </a>
                    </div>
                  </div>
                </div>
              </div>

              {/* Physical address */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-lg">
                    <MapPin className="w-5 h-5" />
                    Physical Address
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-1 text-sm text-muted-foreground">
                  <p>Fig Tree Market</p>
                  <p>123 N Sale St</p>
                  <p>Ellettsville, IN 47404</p>
                </CardContent>
              </Card>

              {/* What to reach out about */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">What We Can Help With</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3 text-sm text-muted-foreground">
                  <p>• General questions about Kalovate</p>
                  <p>• Applying to become a creator or teach classes</p>
                  <p>• Suggesting a class, event, or community contributor</p>
                  <p>• Reporting a bug or technical issue</p>
                  <p>• Partnership and collaboration inquiries</p>
                  <p>• Feedback and feature suggestions</p>
                </CardContent>
              </Card>
            </div>

            {/* Contact Form */}
            <div className="lg:col-span-2">
              <Card>
                <CardHeader>
                  <CardTitle className="text-2xl">Send us a Message</CardTitle>
                  <p className="text-muted-foreground">
                    Fill out the form below and we'll get back to you as soon as possible.
                  </p>
                </CardHeader>
                <CardContent>
                  <form onSubmit={handleSubmit} className="space-y-6">
                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <label className="text-sm font-medium text-foreground mb-2 block">
                          Name *
                        </label>
                        <Input
                          value={formData.name}
                          onChange={e => handleInputChange('name', e.target.value)}
                          placeholder="Your full name"
                          data-testid="input-name"
                          required
                        />
                      </div>

                      <div>
                        <label className="text-sm font-medium text-foreground mb-2 block">
                          Email *
                        </label>
                        <Input
                          type="email"
                          value={formData.email}
                          onChange={e => handleInputChange('email', e.target.value)}
                          placeholder="your@email.com"
                          data-testid="input-email"
                          required
                        />
                      </div>
                    </div>

                    <div>
                      <label className="text-sm font-medium text-foreground mb-2 block">
                        Inquiry Type
                      </label>
                      <select
                        value={formData.type}
                        onChange={e => handleInputChange('type', e.target.value)}
                        className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground"
                        data-testid="select-inquiry-type"
                      >
                        <option value="general">General Inquiry</option>
                        <option value="technical">Technical Support</option>
                        <option value="billing">Billing Question</option>
                        <option value="creator">Creator Support</option>
                        <option value="creator">Create with Kalovate</option>
                        <option value="press">Press Inquiry</option>
                        <option value="feedback">Feedback/Suggestion</option>
                      </select>
                    </div>

                    <div>
                      <label className="text-sm font-medium text-foreground mb-2 block">
                        Subject *
                      </label>
                      <Input
                        value={formData.subject}
                        onChange={e => handleInputChange('subject', e.target.value)}
                        placeholder="Brief description of your inquiry"
                        data-testid="input-subject"
                        required
                      />
                    </div>

                    <div>
                      <label className="text-sm font-medium text-foreground mb-2 block">
                        Message *
                      </label>
                      <Textarea
                        value={formData.message}
                        onChange={e => handleInputChange('message', e.target.value)}
                        placeholder="Please provide details about your inquiry..."
                        rows={6}
                        data-testid="textarea-message"
                        required
                      />
                    </div>

                    <Button
                      type="submit"
                      disabled={contactMutation.isPending}
                      className="w-full bg-primary text-primary-foreground hover:bg-primary/90 font-semibold text-lg py-6"
                      data-testid="button-send-message"
                    >
                      <Send className="w-5 h-5 mr-2" />
                      {contactMutation.isPending ? 'Sending...' : 'Send Message'}
                    </Button>

                    <p className="text-xs text-muted-foreground text-center">
                      By sending this message, you agree to our privacy policy. We'll only use your
                      information to respond to your inquiry.
                    </p>
                  </form>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </div>

      {/* FAQ Section */}
      <section className="py-16 bg-muted">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-foreground mb-4">Need Quick Answers?</h2>
          <p className="text-lg text-muted-foreground mb-8">
            Check out our FAQ section for immediate answers to common questions.
          </p>
          <Button asChild size="lg" data-testid="button-view-faq">
            <a href="/faq">View FAQ</a>
          </Button>
        </div>
      </section>
    </PublicShell>
  );
}
